/**
 * ElectronBridge Service
 * 
 * This service provides a bridge between the Electron main process and the webapp.
 * It listens for IPC messages from the main process and routes them to the appropriate
 * services, particularly DeepPacketAnalyser for header analysis.
 * 
 * The bridge is only active when running in Electron (detected via window.electronAPI).
 */

import { DeepPacketAnalyser } from './DeepPacketAnalyser';
import { createLogger } from '../utils/logger';
const log = createLogger('ElectronBridge');

/** Header data received from Electron main process */
export interface ElectronHeaderData {
    url: string;
    headers: Record<string, string>;
    statusCode: number;
    ttfb: number;
    timestamp: number;
    method: string;
    resourceType: string;
}

/** Server IP resolution data */
export interface ServerIpData {
    url: string;
    hostname: string | null;
    ip: string;
    fromCache: boolean;
    timestamp: number;
}

/** Callback for server IP events */
export type ServerIpCallback = (data: ServerIpData) => void;

/** Traceroute hop data */
export interface TracerouteHop {
    hop: number;
    ip: string | null;
    hostname: string | null;
    rtt: number | null;
}

/** Complete traceroute result with metadata */
export interface TracerouteResult {
    target: string;
    targetHostname: string;
    targetIp: string;
    hops: TracerouteHop[];
    timestamp: number;
    complete: boolean;
}

/** Callback for traceroute completion events */
export type TracerouteCallback = (result: TracerouteResult) => void;

/** Callback for individual traceroute hop events (streaming) */
export type TracerouteHopCallback = (hop: TracerouteHop) => void;

/**
 * Checks if the app is running in Electron
 */
export function isElectron(): boolean {
    return typeof window !== 'undefined' &&
        window.electronAPI !== undefined &&
        window.electronAPI.isElectron === true;
}

/**
 * ElectronBridge singleton - manages IPC communication with Electron main process
 */
export class ElectronBridge {
    private static instance: ElectronBridge;
    private unsubscribeHeaders: (() => void) | null = null;
    private unsubscribeServerIp: (() => void) | null = null;
    private unsubscribeTraceroute: (() => void) | null = null;
    private unsubscribeTracerouteHop: (() => void) | null = null;
    private analyser: DeepPacketAnalyser;

    /** Map of hostname to server IP address */
    private serverIpMap: Map<string, ServerIpData> = new Map();

    /** Callbacks for server IP listeners */
    private serverIpListeners: Set<ServerIpCallback> = new Set();

    /** Map of hostname to traceroute result */
    private tracerouteResults: Map<string, TracerouteResult> = new Map();

    /** Callbacks for traceroute completion */
    private tracerouteListeners: Set<TracerouteCallback> = new Set();

    /** Callbacks for individual traceroute hops (streaming) */
    private tracerouteHopListeners: Set<TracerouteHopCallback> = new Set();

    /** Set of hostnames currently being traced (to avoid duplicates) */
    private pendingTraceroutes: Set<string> = new Set();

    /** Current traceroute target hostname (for result association) */
    private currentTracerouteTarget: { hostname: string; ip: string } | null = null;

    /** Auto-traceroute enabled flag */
    private autoTracerouteEnabled: boolean = true;

    private constructor() {
        this.analyser = DeepPacketAnalyser.getInstance();
    }

    public static getInstance(): ElectronBridge {
        if (!ElectronBridge.instance) {
            ElectronBridge.instance = new ElectronBridge();
        }
        return ElectronBridge.instance;
    }

    /**
     * Initialize the bridge and start listening for IPC messages
     */
    public init(): void {
        if (!isElectron()) {
            log.log('Not running in Electron, skipping initialization');
            return;
        }

        log.log('Initializing bridge...');

        if (window.electronAPI) {
            // Subscribe to header events from main process
            this.unsubscribeHeaders = window.electronAPI.onHttpHeaders((data: ElectronHeaderData) => {
                this.handleHeaders(data);
            });

            // Subscribe to server IP resolution events
            this.unsubscribeServerIp = window.electronAPI.onServerIpResolved((data: ServerIpData) => {
                this.handleServerIp(data);
            });

            // Subscribe to traceroute results from main process
            this.unsubscribeTraceroute = window.electronAPI.onTracerouteResult((result: any) => {
                this.handleTracerouteResult(result);
            });

            // Subscribe to individual traceroute hops (streaming)
            this.unsubscribeTracerouteHop = window.electronAPI.onTracerouteHop((hop: TracerouteHop) => {
                this.handleTracerouteHop(hop);
            });

            log.log('Subscribed to server IP, traceroute, and streaming hop events');
        }

        log.log('Bridge active - CORS bypass, IP tracking & auto-traceroute enabled!');
    }

    /**
     * Handle incoming headers from Electron main process
     */
    private handleHeaders(data: ElectronHeaderData): void {
        // Determine request type based on URL
        const isManifest = data.url.includes('.m3u8') || data.url.includes('.mpd');
        const requestType = isManifest ? 'Manifest' : 'Segment';

        // Route to DeepPacketAnalyser
        this.analyser.analyzeFromElectron(
            data.url,
            data.headers,
            data.ttfb,
            data.timestamp,
            requestType
        );
    }

    /**
     * Handle server IP resolution from Electron main process
     * Triggers auto-traceroute for new video server IPs
     */
    private handleServerIp(data: ServerIpData): void {
        if (!data.hostname) {
            return;
        }

        const existingData = this.serverIpMap.get(data.hostname);
        const isNewServer = !existingData;
        const ipChanged = !!(existingData && existingData.ip !== data.ip);

        // Store in map
        this.serverIpMap.set(data.hostname, data);

        // Notify listeners (always notify so subscribers can re-synch even if IP is cached)
        this.serverIpListeners.forEach(callback => callback(data));

        // Only log for new servers or IP changes (suppress duplicate messages)
        if (isNewServer || ipChanged) {
            log.log(`Server IP resolved: ${data.hostname} → ${data.ip}`);
        }

        // Auto-trigger traceroute for new IPs (if enabled and not already pending/completed)
        if ((isNewServer || ipChanged) && this.autoTracerouteEnabled && !this.pendingTraceroutes.has(data.ip) && !this.tracerouteResults.has(data.ip)) {
            log.log(`Auto-triggering traceroute to ${data.ip} (${data.hostname})`);
            this.pendingTraceroutes.add(data.ip);
            this.currentTracerouteTarget = { hostname: data.hostname || data.ip, ip: data.ip };

            if (window.electronAPI) {
                window.electronAPI.runTraceroute(data.ip);
            }
        }
    }

    /**
     * Handle traceroute result from Electron main process
     * Main process sends: { target, hops, timestamp, complete, error? }
     */
    private handleTracerouteResult(data: any): void {
        const target = this.currentTracerouteTarget;

        if (!target) {
            log.warn('Received traceroute result but no target was set');
            return;
        }

        // Extract hops from the result object
        const hops: TracerouteHop[] = data?.hops || [];

        // Build complete result object with our metadata
        const result: TracerouteResult = {
            target: data?.target || target.ip,
            targetHostname: target.hostname,
            targetIp: target.ip,
            hops,
            timestamp: data?.timestamp || Date.now(),
            complete: data?.complete ?? hops.length > 0
        };

        // Store result by IP (most specific) and Hostname (for lookup)
        this.tracerouteResults.set(target.ip, result);
        if (target.hostname) {
            this.tracerouteResults.set(target.hostname, result);
        }

        // Remove from pending
        this.pendingTraceroutes.delete(target.ip);
        if (target.hostname) {
            this.pendingTraceroutes.delete(target.hostname);
        }

        // Clear current target
        this.currentTracerouteTarget = null;

        log.log(`Traceroute complete for ${target.ip} (${target.hostname}): ${hops.length} hops`);

        // Notify listeners
        this.tracerouteListeners.forEach(callback => callback(result));
    }

    /**
     * Handle individual traceroute hop (streaming)
     * Called for each hop as it's discovered
     */
    private handleTracerouteHop(hop: TracerouteHop): void {
        log.log(`Streaming hop ${hop.hop}: ${hop.ip || hop.hostname || '*'}`);

        // Notify hop listeners
        this.tracerouteHopListeners.forEach(callback => callback(hop));
    }

    /**
     * Subscribe to individual traceroute hop events (streaming)
     * @returns Unsubscribe function
     */
    public onTracerouteHop(callback: TracerouteHopCallback): () => void {
        this.tracerouteHopListeners.add(callback);
        return () => this.tracerouteHopListeners.delete(callback);
    }

    /**
     * Subscribe to server IP resolution events
     * @returns Unsubscribe function
     */
    public onServerIpResolved(callback: ServerIpCallback): () => void {
        this.serverIpListeners.add(callback);
        return () => this.serverIpListeners.delete(callback);
    }

    /**
     * Get the IP address for a given hostname
     */
    public getServerIp(hostname: string): string | null {
        return this.serverIpMap.get(hostname)?.ip || null;
    }

    /**
     * Get all known server IPs
     */
    public getAllServerIps(): Map<string, ServerIpData> {
        return new Map(this.serverIpMap);
    }

    /**
     * Run a traceroute to the given host (IP or hostname)
     * Results will be delivered via onTracerouteResult callback
     */
    public runTraceroute(host: string): void {
        if (!isElectron() || !window.electronAPI) {
            log.warn('Traceroute only available in Electron');
            return;
        }

        log.log(`Starting traceroute to ${host}...`);
        window.electronAPI.runTraceroute(host);
    }

    /**
     * Run traceroute to a known video server by hostname
     */
    public runTracerouteToServer(hostname: string): void {
        const ip = this.getServerIp(hostname);
        if (ip) {
            this.runTraceroute(ip);
        } else {
            log.warn(`No IP found for hostname: ${hostname}`);
            // Try with hostname directly
            this.runTraceroute(hostname);
        }
    }

    /**
     * Subscribe to traceroute completion events
     * @returns Unsubscribe function
     */
    public onTracerouteComplete(callback: TracerouteCallback): () => void {
        this.tracerouteListeners.add(callback);
        return () => this.tracerouteListeners.delete(callback);
    }

    /**
     * Get stored traceroute result for a hostname
     */
    public getTracerouteResult(hostname: string): TracerouteResult | null {
        return this.tracerouteResults.get(hostname) || null;
    }

    /**
     * Get all stored traceroute results
     */
    public getAllTracerouteResults(): Map<string, TracerouteResult> {
        return new Map(this.tracerouteResults);
    }

    /**
     * Enable or disable auto-traceroute on server discovery
     */
    public setAutoTraceroute(enabled: boolean): void {
        this.autoTracerouteEnabled = enabled;
        log.log(`Auto-traceroute ${enabled ? 'enabled' : 'disabled'}`);
    }

    /**
     * Check if a traceroute is currently pending for a hostname
     */
    public isTraceroutePending(hostname: string): boolean {
        return this.pendingTraceroutes.has(hostname);
    }

    /**
     * Reset all internal caches and trigger main process cache reset.
     * Use this when switching streams or starting a fresh analysis.
     */
    public reset(): void {
        this.serverIpMap.clear();
        this.tracerouteResults.clear();
        this.pendingTraceroutes.clear();
        this.currentTracerouteTarget = null;

        if (isElectron() && window.electronAPI) {
            window.electronAPI.resetNetworkCache();
        }
        log.log('All network caches and traceroute results reset');
    }

    /**
     * Cleanup and stop listening for IPC messages
     */
    public destroy(): void {
        if (this.unsubscribeHeaders) {
            this.unsubscribeHeaders();
            this.unsubscribeHeaders = null;
        }
        if (this.unsubscribeServerIp) {
            this.unsubscribeServerIp();
            this.unsubscribeServerIp = null;
        }
        if (this.unsubscribeTraceroute) {
            this.unsubscribeTraceroute();
            this.unsubscribeTraceroute = null;
        }
        if (this.unsubscribeTracerouteHop) {
            this.unsubscribeTracerouteHop();
            this.unsubscribeTracerouteHop = null;
        }
        this.serverIpMap.clear();
        this.serverIpListeners.clear();
        this.tracerouteResults.clear();
        this.tracerouteListeners.clear();
        this.tracerouteHopListeners.clear();
        this.pendingTraceroutes.clear();
        this.currentTracerouteTarget = null;
        log.log('Bridge destroyed');
    }
}

// Type declaration for Electron API (must match preload.ts)
declare global {
    interface Window {
        electronAPI?: {
            isElectron: boolean;
            platform: string;
            onHttpHeaders: (callback: (data: ElectronHeaderData) => void) => () => void;
            onServerIpResolved: (callback: (data: { url: string; hostname: string | null; ip: string; fromCache: boolean; timestamp: number }) => void) => () => void;
            onTracerouteHop: (callback: (hop: TracerouteHop) => void) => () => void;
            onTracerouteResult: (callback: (result: any) => void) => () => void;
            runTraceroute: (host: string) => void;
            getPublicIP: () => Promise<string>;
            getGeolocation: () => Promise<{ lat: number; lon: number; accuracy: number } | null>;
            setCustomHeaders: (headers: Record<string, string>) => void;
            clearCustomHeaders: () => void;
            resetNetworkCache: () => void;
            onUpdateChecking: (callback: () => void) => () => void;
            onUpdateAvailable: (callback: (info: { version: string; releaseNotes: string | null }) => void) => () => void;
            onUpdateProgress: (callback: (progress: { percent: number; transferred: number; total: number; bytesPerSecond: number }) => void) => () => void;
            onUpdateDownloaded: (callback: (info: { version: string }) => void) => () => void;
            onUpdateError: (callback: (error: { message: string }) => void) => () => void;
            installUpdate: () => void;
        };
    }
}
